// Supplier / branch prioritisation state for the search page (DEV-50739).
//
// Two independent pieces:
//   1. WHICH pills exist — derived purely from the server-side basket. One
//      entry per supplier+branch that holds at least one cart line. Nothing
//      is stored for this; it is recomputed from `useCart()` every render.
//   2. WHICH pills are ticked — a list of pill keys kept in localStorage,
//      scoped per tenant and per user. Before it is exposed it is filtered
//      against the live basket, so removing the last item at a branch drops
//      that branch's tick with no syncing code and no effect: the next read
//      simply no longer finds it.
import { useCallback, useMemo, useSyncExternalStore } from 'react'
import { useParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { useCart } from '../auth/hooks/useCart'
import { useMe } from '../auth/hooks/useMe'
import { api } from '../services/api'
import {
  parseEngineerSession,
  readEngineerSessionRaw,
  subscribeToEngineerSession,
} from '../services/engineerSession'
import type {
  AtBranchSource,
  EngineerSessionAtBranch,
} from '../services/engineerSession'
import {
  fulfillmentKey,
  readCartFulfillmentMap,
  readItemFulfillmentMap,
  type CartFulfillmentMethod,
} from '../utils/cartFulfillment'
import type { CartItem } from '../types'
import {
  BRANCH_PRIORITY_ELIGIBILITY_MAX_IDS,
  BRANCH_PRIORITY_KEY_SEPARATOR,
  BRANCH_PRIORITY_STORAGE_PREFIX,
} from '../constants/branchPriority'

/** One supplier+branch a pill represents — from the basket, or from DEV-51045
 * the counter the engineer said they were standing at. */
export interface BasketBranch {
  key: string
  supplierId: string
  supplierName: string
  branchId: string
  branchName: string
  // Set for the counter attached to the engineer's location. Its pill
  // survives an empty basket and leads the row. The value says how it was
  // found, because only `device` justifies claiming they are standing there.
  here?: AtBranchSource
  // Miles from the basket's postcode, as stored on the cart line (null when
  // unknown). Shown on prioritised search cards next to the branch name.
  distanceMiles: number | null
  // Every fulfilment method used by the lines at this branch, in first-seen
  // order. Collection and delivery at the same branch share one pill; the
  // "+N more" sheet (SP-002) lists the methods after the branch name.
  methods: CartFulfillmentMethod[]
}

export function branchPriorityKey(
  supplierId: string | number,
  branchId: string | number,
): string {
  return `${supplierId}${BRANCH_PRIORITY_KEY_SEPARATOR}${branchId}`
}

// Resolve a cart line's fulfilment method the same way the Cart page does:
// backend value first, then the per-line bridge, then the per-branch bridge
// written by ProductDetail on add, else pickup.
function resolveLineMethod(
  item: CartItem,
  itemMap: Record<string, CartFulfillmentMethod>,
  groupMap: Record<string, CartFulfillmentMethod>,
): CartFulfillmentMethod {
  if (item.fulfillmentMethod) return item.fulfillmentMethod
  if (item.id && itemMap[item.id]) return itemMap[item.id]
  return groupMap[fulfillmentKey(item.supplierId, item.branchId)] ?? 'pickup'
}

/**
 * Group basket lines into one entry per supplier+branch, in the order the
 * branches first appear in the basket (so the visible pills don't reshuffle
 * as the engineer taps them). Lines with no branch, or from a branch the
 * basket already flags as closed, are skipped.
 */
export function getBasketBranches(cart: CartItem[]): BasketBranch[] {
  const itemMap = readItemFulfillmentMap()
  const groupMap = readCartFulfillmentMap()
  const branches = new Map<string, BasketBranch>()
  for (const item of cart) {
    if (item.branchId == null || item.branchIsActive === false) continue
    const key = branchPriorityKey(item.supplierId, item.branchId)
    const method = resolveLineMethod(item, itemMap, groupMap)
    const existing = branches.get(key)
    if (existing) {
      if (!existing.methods.includes(method)) existing.methods.push(method)
      continue
    }
    branches.set(key, {
      key,
      supplierId: String(item.supplierId),
      supplierName: item.supplierName,
      branchId: String(item.branchId),
      branchName: item.branchName,
      distanceMiles: item.distanceMiles ?? null,
      methods: [method],
    })
  }
  return [...branches.values()]
}

/**
 * Add the branches the engineer chose at their location to the pill set,
 * first and in the order given, even when the basket holds nothing from them
 * (DEV-51045 AC4).
 *
 * Each is merged rather than appended when it is already a basket branch, so
 * it shows once. Ticks for every other branch still require a basket line —
 * the filter below is unchanged, it simply has more valid pills to recognise.
 */
export function withLocationBranches(
  branches: BasketBranch[],
  atBranches: EngineerSessionAtBranch[],
): BasketBranch[] {
  if (atBranches.length === 0) return branches
  const hereKeys = new Set<string>()
  const here: BasketBranch[] = []
  for (const atBranch of atBranches) {
    const key = branchPriorityKey(atBranch.supplierId, atBranch.branchId)
    if (hereKeys.has(key)) continue
    hereKeys.add(key)
    const inBasket = branches.find((branch) => branch.key === key)
    here.push(
      inBasket
        ? { ...inBasket, here: atBranch.source }
        : {
            key,
            supplierId: String(atBranch.supplierId),
            supplierName: atBranch.supplierName,
            branchId: String(atBranch.branchId),
            branchName: atBranch.branchName,
            // No basket line, so no distance and no fulfilment method. Both
            // render as "here" / omitted rather than blank.
            distanceMiles: null,
            methods: [],
            here: atBranch.source,
          },
    )
  }
  return [...here, ...branches.filter((branch) => !hereKeys.has(branch.key))]
}

interface StoredBranchPriority {
  keys: string[]
}

const EMPTY_STORED: StoredBranchPriority = { keys: [] }

const onlyStrings = (values: unknown): string[] =>
  Array.isArray(values)
    ? values.filter((value): value is string => typeof value === 'string')
    : []

// ── Shared store ─────────────────────────────────────────────────────────
// The pill row, the sheet and the search page each call the hook. They must
// all see one write at once, so the stored value is read through a single
// external store: any write notifies every subscriber (and other tabs
// arrive via the browser's storage event). Without this each caller would
// hold its own stale copy until it remounted.
const storeListeners = new Set<() => void>()

function subscribeToStore(listener: () => void): () => void {
  storeListeners.add(listener)
  window.addEventListener('storage', listener)
  return () => {
    storeListeners.delete(listener)
    window.removeEventListener('storage', listener)
  }
}

function notifyStore(): void {
  for (const listener of storeListeners) listener()
}

// In-memory fallback for keys whose last localStorage write failed
// (storage disabled, full, or blocked for privacy). Holds the serialised
// value (or null for "cleared") so ticks still work for this page life;
// a later successful write clears the override and storage rules again.
const memoryFallback = new Map<string, string | null>()

// Snapshot is the raw string so React's equality check is stable; parsing
// happens in a memo keyed on it.
function readRawStored(storageKey: string | null): string | null {
  if (storageKey === null || typeof window === 'undefined') return null
  if (memoryFallback.has(storageKey)) return memoryFallback.get(storageKey) ?? null
  try {
    return window.localStorage.getItem(storageKey)
  } catch {
    return null
  }
}

function parseStored(raw: string | null): StoredBranchPriority {
  if (!raw) return EMPTY_STORED
  try {
    const parsed = JSON.parse(raw) as unknown
    // SP-001 shipped a bare array of keys; keep reading that shape. Later
    // versions stored { keys, exploredSearchKey } — the extra field (now
    // retired) is simply ignored.
    if (Array.isArray(parsed)) return { keys: onlyStrings(parsed) }
    if (!parsed || typeof parsed !== 'object') return EMPTY_STORED
    const record = parsed as Partial<StoredBranchPriority>
    return { keys: onlyStrings(record.keys) }
  } catch {
    // Corrupt or blocked storage — start unticked rather than break search.
    return EMPTY_STORED
  }
}

function writeStored(storageKey: string, value: StoredBranchPriority): void {
  if (typeof window === 'undefined') return
  const serialised = value.keys.length === 0 ? null : JSON.stringify(value)
  try {
    if (serialised === null) {
      window.localStorage.removeItem(storageKey)
    } else {
      window.localStorage.setItem(storageKey, serialised)
    }
    memoryFallback.delete(storageKey)
  } catch {
    // Storage full / disabled — keep the value in memory so subscribers
    // read the new snapshot and the tap still works for this page life.
    memoryFallback.set(storageKey, serialised)
  }
  notifyStore()
}

/**
 * The per-tenant, per-user key the ticks are stored under, or null while the
 * user is unknown. Exported so the location flow can tick the counter the
 * engineer confirmed without mounting the hook.
 */
export function buildBranchPriorityStorageKey(
  tenantSlug: string | undefined,
  userId: string | undefined,
): string | null {
  if (!userId) return null
  return `${BRANCH_PRIORITY_STORAGE_PREFIX}_${tenantSlug ?? 'default'}_${userId}`
}

/**
 * Tick one branch from outside the pill row — used when the engineer
 * confirms a location at a counter (DEV-51045 AC4). Existing ticks are kept.
 *
 * Safe to persist despite the counter being session-only: its pill comes
 * from the session, so next session there is no pill and the filter drops
 * the tick on the next write. Nothing needs expiring.
 */
export function tickBranchPriority(
  storageKey: string | null,
  key: string,
): void {
  if (!storageKey) return
  const stored = parseStored(readRawStored(storageKey))
  if (stored.keys.includes(key)) return
  writeStored(storageKey, { keys: [...stored.keys, key] })
}

/** Drop one tick — e.g. the counter the engineer has just moved away from. */
export function untickBranchPriority(
  storageKey: string | null,
  key: string,
): void {
  if (!storageKey) return
  const stored = parseStored(readRawStored(storageKey))
  if (!stored.keys.includes(key)) return
  writeStored(storageKey, {
    keys: stored.keys.filter((stored_key) => stored_key !== key),
  })
}

/**
 * Basket-derived branch pills and their persisted tick state.
 *
 * `isReady` is false until both the basket and the current user have
 * loaded; callers should render nothing for the row until then so an empty
 * row never flashes before the basket arrives.
 */
export function useBranchPriority() {
  const { cart, isLoading: isCartLoading } = useCart()
  const { data: meData } = useMe()
  const { tenantSlug } = useParams<{ tenantSlug: string }>()

  // Storage key is only meaningful once the user id is known; until then
  // nothing is read or written so one user's ticks never land under
  // another's key on a shared device.
  const storageKey = buildBranchPriorityStorageKey(tenantSlug, meData?.id)

  // The branches the engineer chose at their location, from the session.
  const rawSession = useSyncExternalStore(
    subscribeToEngineerSession,
    readEngineerSessionRaw,
    () => null,
  )
  const atBranches: EngineerSessionAtBranch[] = useMemo(
    () => parseEngineerSession(rawSession)?.atBranches ?? [],
    [rawSession],
  )

  // Every hook instance reads the same store snapshot, so a write from the
  // pill row is seen by the search page in the same render pass.
  const rawStored = useSyncExternalStore(
    subscribeToStore,
    () => readRawStored(storageKey),
    () => null,
  )
  const stored = useMemo(() => parseStored(rawStored), [rawStored])
  const storedKeys = stored.keys

  const persist = useCallback(
    (value: StoredBranchPriority) => {
      if (storageKey) writeStored(storageKey, value)
    },
    [storageKey],
  )

  const allBasketBranches = useMemo(() => getBasketBranches(cart), [cart])

  // Role eligibility (ROLE-004/005): a supplier or branch restricted AFTER
  // the item was added keeps its basket line but must not offer a pill.
  // Asked once per visit to the page, in parallel with the search's own
  // requests (never in front of them); pills render from the basket at
  // once and any ineligible one drops out when the answer lands. A failed
  // call (null) hides nothing. Per-tenant key + no stale reuse across
  // visits, so an admin change applies on the next page load.
  const candidateBranchIds = useMemo(
    () => [...new Set(allBasketBranches.map((branch) => branch.branchId))].sort(),
    [allBasketBranches],
  )
  const { data: allowedBranchIds } = useQuery({
    queryKey: ['branch-priority-eligibility', tenantSlug ?? 'default', candidateBranchIds],
    queryFn: ({ signal }) => api.getBranchPriorityEligibility(candidateBranchIds, signal),
    enabled: candidateBranchIds.length > 0,
    staleTime: 0,
    refetchOnWindowFocus: false,
    retry: 0,
  })

  // The location's branches (DEV-51045) are checked in their own request
  // rather than added to the basket's. The endpoint takes at most 50 ids and
  // fails open when refused, so appending them to a full basket would turn a
  // working check into one that keeps restricted branches visible. Separate,
  // the basket check is exactly what it was before this feature. There are
  // only ever a handful — whatever was within a quarter mile — so this one
  // stays far below the limit. Same rules and same fail-open answer, so a
  // branch restricted after the location was set still loses its pill on the
  // next page load.
  const atBranchIds = useMemo(
    () => [...new Set(atBranches.map((branch) => String(branch.branchId)))].sort(),
    [atBranches],
  )
  const { data: atBranchAllowedIds } = useQuery({
    queryKey: [
      'branch-priority-eligibility',
      tenantSlug ?? 'default',
      'at-branch',
      atBranchIds,
    ],
    queryFn: async ({ signal }) => {
      // The endpoint refuses more than BRANCH_PRIORITY_ELIGIBILITY_MAX_IDS
      // ids, and a refusal fails open — so an oversized list would skip the
      // check entirely. Split into batches sent in parallel. In practice this
      // is always a single batch (a quarter mile never holds anywhere near
      // 50 branches), so the normal case is one request, exactly as before.
      const batches: string[][] = []
      for (
        let start = 0;
        start < atBranchIds.length;
        start += BRANCH_PRIORITY_ELIGIBILITY_MAX_IDS
      ) {
        batches.push(
          atBranchIds.slice(start, start + BRANCH_PRIORITY_ELIGIBILITY_MAX_IDS),
        )
      }
      const answers = await Promise.all(
        batches.map((batch) => api.getBranchPriorityEligibility(batch, signal)),
      )
      // Any unanswered batch leaves the whole answer unknown, which reads as
      // allowed — the same fail-open rule the basket check uses.
      if (answers.some((answer) => answer === null)) return null
      return answers.flatMap((answer) => answer ?? [])
    },
    enabled: atBranchIds.length > 0,
    staleTime: 0,
    refetchOnWindowFocus: false,
    retry: 0,
  })

  const basketBranches = useMemo(() => {
    const allowed = allowedBranchIds ? new Set(allowedBranchIds) : null
    const eligibleBasket = allowed
      ? allBasketBranches.filter((branch) => allowed.has(branch.branchId))
      : allBasketBranches
    // Unanswered or failed reads as allowed, matching the basket check.
    const allowedHere = atBranchAllowedIds ? new Set(atBranchAllowedIds) : null
    const eligibleHere = allowedHere
      ? atBranches.filter((branch) => allowedHere.has(String(branch.branchId)))
      : atBranches
    // The location's branches lead the row and survive an empty basket.
    return withLocationBranches(eligibleBasket, eligibleHere)
  }, [allBasketBranches, allowedBranchIds, atBranches, atBranchAllowedIds])

  // Ticks are only honoured for branches that are still in the basket.
  const selectedKeys = useMemo(() => {
    if (basketBranches.length === 0) return []
    const basketKeys = new Set(basketBranches.map((branch) => branch.key))
    return storedKeys.filter((key) => basketKeys.has(key))
  }, [storedKeys, basketBranches])

  const selectedBranches = useMemo(
    () => basketBranches.filter((branch) => selectedKeys.includes(branch.key)),
    [basketBranches, selectedKeys],
  )

  const applyKeys = useCallback(
    (keys: string[]) => {
      // Persist in basket order and drop anything not in the basket, so the
      // stored list is always a clean subset of the current pills.
      const next = basketBranches
        .map((branch) => branch.key)
        .filter((key) => keys.includes(key))
      persist({ keys: next })
    },
    [basketBranches, persist],
  )

  const toggleKey = useCallback(
    (key: string) => {
      applyKeys(
        selectedKeys.includes(key)
          ? selectedKeys.filter((selected) => selected !== key)
          : [...selectedKeys, key],
      )
    },
    [applyKeys, selectedKeys],
  )

  const clearKeys = useCallback(() => applyKeys([]), [applyKeys])

  return {
    isReady: !isCartLoading && storageKey !== null,
    basketBranches,
    selectedKeys,
    selectedBranches,
    applyKeys,
    toggleKey,
    clearKeys,
  }
}
